import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { apiCall, API, WEEKDAYS, fmtDate } from "./calendar/calendarUtils";
import { WindowsEditor } from "./availability/WindowsEditor";
import { PeriodDialog, KINDS } from "./availability/PeriodDialog";
import { ConflictsPanel } from "./calendar/ConflictsPanel";
import { AppointmentDialog } from "./calendar/AppointmentDialog";

const CATS = [["einzel", "Einzeltraining", "Einzeltraining Kind, Einzeltraining Erwachsen, Torwarttraining"], ["gruppe", "Gruppentraining", "Gruppentraining 2–4 und ab 5 Spieler"]];
const MODE_TEXT = (r) => (r.mode === "blocked" ? "gesperrt" : r.mode === "window" ? r.windows.map((w) => `${w.start}–${w.end}`).join(", ") : "regulär");

function WeeklyCard({ cat, label, hint, weekly, onChange }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 p-4" data-testid={`weekly-${cat}`}>
      <h4 className="font-['Bebas_Neue'] text-xl text-white">{label}</h4>
      <p className="text-xs text-[#71717A] mb-3">{hint}</p>
      <div className="space-y-2">
        {WEEKDAYS.map((d, i) => (
          <div key={d} className="flex flex-wrap items-center gap-3 py-1 border-b border-white/5">
            <span className="w-24 text-sm text-white">{d}</span>
            <WindowsEditor windows={weekly[String(i)] || []} onChange={(w) => onChange({ ...weekly, [String(i)]: w })} testId={`weekly-${cat}-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VerfuegbarkeitenTab({ adminToken: token }) {
  const [rules, setRules] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [priority, setPriority] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([apiCall(token, "/booking-rules/admin"), apiCall(token, "/booking-rules/admin/conflicts")]);
      setRules(r.rules); setPeriods(r.periods); setPriority(r.priority); setConflicts(c.conflicts);
    } catch (err) { toast.error(err.message); }
  }, [token]);

  useEffect(() => { load(); fetch(`${API}/appointments/training-types`).then((r) => r.json()).then((d) => setTypes(d.training_types || [])); }, [load]);

  const saveWeekly = async () => {
    try { await apiCall(token, "/booking-rules/admin/weekly", { method: "PUT", body: rules.weekly }); toast.success("Wöchentliche Zeiten gespeichert"); load(); } catch (err) { toast.error(err.message); }
  };
  const saveSettings = async () => {
    try { await apiCall(token, "/booking-rules/admin/settings", { method: "PUT", body: { min_lead_hours: Number(rules.min_lead_hours), max_weeks_ahead: Number(rules.max_weeks_ahead) } }); toast.success("Einstellungen gespeichert"); load(); } catch (err) { toast.error(err.message); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Zeitraum „${p.name}" löschen? Bestehende Buchungen bleiben unverändert.`)) return;
    try { await apiCall(token, `/booking-rules/admin/periods/${p.id}`, { method: "DELETE" }); toast.success("Zeitraum gelöscht"); load(); } catch (err) { toast.error(err.message); }
  };
  const openById = async (id) => setSelected(await apiCall(token, `/calendar/admin/appointments/${id}`));

  if (!rules) return <p className="text-[#71717A] py-8 text-center">Laden…</p>;

  return (
    <div className="space-y-8" data-testid="verfuegbarkeiten-tab">
      <div className="flex items-start gap-3 bg-[#c1ff72]/5 border border-[#c1ff72]/20 p-4 text-sm text-[#D4D4D8]" data-testid="rules-priority">
        <ShieldCheck className="w-5 h-5 text-[#c1ff72] flex-shrink-0" />
        <div><span className="text-[#c1ff72] font-semibold">Prioritäten (von oben nach unten):</span> {priority.join(" → ")}. Alle Regeln werden bei jeder Buchung, Bestätigung und Verschiebung serverseitig geprüft (Zeitzone Europe/Berlin).</div>
      </div>

      <ConflictsPanel conflicts={conflicts} onOpen={openById} />

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="font-['Bebas_Neue'] text-2xl text-white">Sonderzeiträume, Camps & Urlaub</h3>
          <Button onClick={() => setEditing({})} className="bg-[#c1ff72] text-black hover:bg-[#a8e65c] rounded-none" data-testid="period-add"><Plus className="w-4 h-4 mr-1" />Zeitraum eintragen</Button>
        </div>
        <div className="space-y-2" data-testid="periods-list">
          {!periods.length && <p className="text-[#71717A] text-sm">Keine Sonderzeiträume eingetragen.</p>}
          {periods.map((p) => (
            <div key={p.id} className={`bg-[#1A1A1A] border border-white/10 p-3 flex flex-wrap items-center gap-3 ${p.expired ? "opacity-50" : ""}`} data-testid={`period-${p.id}`}>
              <span className="text-xs uppercase tracking-wider bg-orange-500/15 text-orange-300 px-2 py-0.5">{KINDS[p.kind]}</span>
              <span className="flex-1 min-w-[200px]">
                <span className="block text-white font-medium">{p.name}{p.expired ? " (abgelaufen)" : ""}</span>
                <span className="block text-xs text-[#A1A1AA]">{fmtDate(p.date_from)} – {fmtDate(p.date_to)}{p.weekdays?.length ? ` · nur ${p.weekdays.map((d) => WEEKDAYS[d].slice(0, 2)).join(", ")}` : ""} · Einzel: {MODE_TEXT(p.einzel)} · Gruppe: {MODE_TEXT(p.gruppe)}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setEditing(p)} className="text-white" data-testid={`period-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-red-400" data-testid={`period-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="font-['Bebas_Neue'] text-2xl text-white">Reguläre Trainingszeiten je Wochentag</h3>
          <Button onClick={saveWeekly} className="bg-[#c1ff72] text-black hover:bg-[#a8e65c] rounded-none" data-testid="weekly-save">Zeiten speichern</Button>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          {CATS.map(([c, label, hint]) => (
            <WeeklyCard key={c} cat={c} label={label} hint={hint} weekly={rules.weekly[c]} onChange={(w) => setRules({ ...rules, weekly: { ...rules.weekly, [c]: w } })} />
          ))}
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#1A1A1A] border border-white/10 p-4 space-y-3" data-testid="booking-settings">
          <h4 className="font-['Bebas_Neue'] text-xl text-white">Vorlauf & Buchungsfenster</h4>
          <div><Label className="text-xs text-[#A1A1AA]">Mindestvorlaufzeit (Stunden)</Label><Input type="number" min={0} value={rules.min_lead_hours} onChange={(e) => setRules({ ...rules, min_lead_hours: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white" data-testid="setting-lead" /></div>
          <div><Label className="text-xs text-[#A1A1AA]">Maximal buchbar im Voraus (Wochen)</Label><Input type="number" min={1} value={rules.max_weeks_ahead} onChange={(e) => setRules({ ...rules, max_weeks_ahead: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white" data-testid="setting-weeks" /></div>
          <Button onClick={saveSettings} className="bg-[#c1ff72] text-black hover:bg-[#a8e65c] rounded-none" data-testid="settings-save">Speichern</Button>
        </div>
        <div className="bg-[#1A1A1A] border border-white/10 p-4" data-testid="services-info">
          <h4 className="font-['Bebas_Neue'] text-xl text-white mb-2">Leistungen & Dauer</h4>
          <p className="text-xs text-[#71717A] mb-2">Wählbare Dauer: 60, 90 oder 120 Minuten. Ein Termin ist nur buchbar, wenn die gesamte Dauer ins Zeitfenster passt. Bereits gebuchte Zeiten werden automatisch blockiert.</p>
          {types.map((t) => <div key={t.id} className="flex justify-between text-sm py-1 border-b border-white/5"><span className="text-white">{t.name}</span><span className="text-[#A1A1AA]">{t.id.startsWith("gruppentraining") ? "Gruppe" : "Einzel"} · {t.base_price} €{t.per_player ? " p. P." : ""} / 60 Min</span></div>)}
        </div>
      </section>

      {editing && <PeriodDialog token={token} period={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <AppointmentDialog token={token} event={selected} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }} />
    </div>
  );
}
